import React, { useRef, useState } from 'react';
import { Sketch } from '@uiw/react-color';
import TextField from 'Components/DesignSystem/TextField/TextField';
import { useDismissable } from 'Components/DesignSystem/useDismissable';

interface ColourFieldProps {
  testId: string;
  ariaLabel: string;
  value: string;
  onChange: (hex: string) => void;
  error?: string | null;
  disabled?: boolean;
}

/** Swatch button + hex text field + `Sketch` colour-picker popover. */
const ColourField: React.FC<ColourFieldProps> = ({ testId, ariaLabel, value, onChange, error, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useDismissable({
    isOpen: open,
    onDismiss: () => setOpen(false),
    isInside: (target) => Boolean(ref.current?.contains(target)),
  });

  return (
    <div className="tw-relative tw-flex tw-items-center tw-gap-2" ref={ref}>
      <button
        type="button"
        aria-label={`Pick colour — ${ariaLabel}`}
        className="tw-inline-block tw-w-6 tw-h-6 tw-rounded-ctl tw-border tw-border-edge tw-cursor-pointer disabled:tw-cursor-not-allowed"
        style={{ backgroundColor: value || 'transparent' }}
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        data-testid={`${testId}-swatch`}
      />
      <TextField
        label=""
        value={value}
        onChange={onChange}
        error={Boolean(error)}
        helperText={error ?? undefined}
        disabled={disabled}
        data-testid={testId}
      />
      {open && (
        <div className="tw-absolute tw-top-full tw-left-0 tw-z-50 tw-mt-1" data-testid={`${testId}-palette`}>
          <Sketch color={value || '#000000'} onChange={(next) => onChange(next.hex)} />
        </div>
      )}
    </div>
  );
};

export default ColourField;
